import { useQuoteStore } from '../store'
import { clsx } from 'clsx'
import { AlertTriangle, CheckCircle } from 'lucide-react'

// Helper to format currency
const formatCurrency = (value: number, currency: string): string => {
  const code = currency || 'AUD'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export function QuoteSummary() {
  const { calculations, details, selectedProducts } = useQuoteStore()
  const { displayCurrency } = details

  // Calculate products total
  const productsMonthly = selectedProducts.reduce((sum, p) => sum + (p.volume * p.effectivePrice), 0) / 12

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 sticky top-4">
      <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Quote Summary</h3>

      {/* Key Metrics */}
      {calculations && (
        <>
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-[var(--text-muted)]">MRR</span>
              <span className="text-base font-semibold text-primary">
                {formatCurrency(calculations.mrr, displayCurrency)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-[var(--text-muted)]">ARR</span>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {formatCurrency(calculations.arr, displayCurrency)}
              </span>
            </div>
            <div className="flex items-center justify-between bg-primary/10 -mx-4 px-4 py-2">
              <span className="text-[11px] text-primary/80">TCV</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(calculations.tcv, displayCurrency)}
              </span>
            </div>
          </div>

          <hr className="border-[var(--border-color)] my-4" />

          {/* Totals Breakdown */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--text-muted)]">Products ({selectedProducts.length})</span>
              <span className="text-[var(--text-primary)]">{formatCurrency(productsMonthly, displayCurrency)}/mo</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--text-muted)]">Platform Fee</span>
              <span className="text-[var(--text-primary)]">{formatCurrency(details.platformFees / 12, displayCurrency)}/mo</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--text-muted)]">Implementation</span>
              <span className="text-[var(--text-primary)]">{formatCurrency(details.implementationFee, displayCurrency)}</span>
            </div>
          </div>

          {/* Min-commit reconciliation chip.
              Shows the gap between estimated usage and min commit so the
              reviewer can spot deals where the commit floor is inflating
              year-1 margins. */}
          {details.minimumCommitment > 0 && (
            <div
              className={clsx(
                'rounded-lg p-3 mb-4 text-[11px] flex items-start gap-2',
                calculations.minCommitShortfall > 0
                  ? 'bg-warning/10 border border-warning/30 text-warning'
                  : 'bg-success/10 border border-success/30 text-success',
              )}
            >
              {calculations.minCommitShortfall > 0 ? (
                <AlertTriangle size={14} className="flex-shrink-0 mt-[1px]" />
              ) : (
                <CheckCircle size={14} className="flex-shrink-0 mt-[1px]" />
              )}
              <div className="space-y-1">
                <div>
                  Estimated annual usage:{' '}
                  <strong>{formatCurrency(calculations.usageRevenue, displayCurrency)}</strong>
                </div>
                <div>
                  Min commitment:{' '}
                  <strong>{formatCurrency(details.minimumCommitment, displayCurrency)}</strong>
                </div>
                {calculations.minCommitShortfall > 0 ? (
                  <div className="pt-1 leading-snug opacity-90">
                    Usage is <strong>{formatCurrency(calculations.minCommitShortfall, displayCurrency)} below</strong>{' '}
                    min commit. Customer pays the higher of the two — they'd effectively buy{' '}
                    <strong>{formatCurrency(calculations.minCommitShortfall, displayCurrency)}</strong> of unused capacity.
                  </div>
                ) : (
                  <div className="pt-1 leading-snug opacity-90">
                    Usage exceeds min commit by{' '}
                    <strong>{formatCurrency(calculations.usageRevenue - details.minimumCommitment, displayCurrency)}</strong>.
                  </div>
                )}
              </div>
            </div>
          )}

          <hr className="border-[var(--border-color)] my-4" />

          {/* Health Indicators — split margin display.
              Year-1 margin uses TCV (folds in min commit + implementation).
              Steady-state margin uses usage revenue alone, showing the unit
              economics once customer consumption matches their commit.
              When they match, the two numbers are equal. */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-[11px]">
              <span
                className="text-[var(--text-muted)]"
                title="Margin over the full contract: revenue (max of usage or min commit) + platform fees + implementation, minus all costs."
              >
                Year-1 Margin
              </span>
              <span
                className={clsx(
                  'font-medium',
                  calculations.grossProfitMargin >= 70
                    ? 'text-success'
                    : calculations.grossProfitMargin >= 60
                      ? 'text-info'
                      : calculations.grossProfitMargin >= 50
                        ? 'text-warning'
                        : 'text-danger'
                )}
              >
                {calculations.grossProfitMargin.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span
                className="text-[var(--text-muted)]"
                title="Margin on usage revenue + platform fees only. Ignores the min commit floor — reflects unit economics when consumption matches commit."
              >
                Steady-state Margin
              </span>
              <span
                className={clsx(
                  'font-medium',
                  calculations.steadyStateMarginPct >= 70
                    ? 'text-success'
                    : calculations.steadyStateMarginPct >= 60
                      ? 'text-info'
                      : calculations.steadyStateMarginPct >= 50
                        ? 'text-warning'
                        : 'text-danger'
                )}
              >
                {calculations.steadyStateMarginPct.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--text-muted)]">LTV/CAC</span>
              <span
                className={clsx(
                  'font-medium',
                  calculations.ltvCacRatio >= 4
                    ? 'text-success'
                    : calculations.ltvCacRatio >= 3
                      ? 'text-info'
                      : calculations.ltvCacRatio >= 2
                        ? 'text-warning'
                        : 'text-danger'
                )}
              >
                {calculations.ltvCacRatio.toFixed(1)}x
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[var(--text-muted)]">Payback</span>
              <span
                className={clsx(
                  'font-medium',
                  calculations.paybackMonths <= 9
                    ? 'text-success'
                    : calculations.paybackMonths <= 12
                      ? 'text-info'
                      : calculations.paybackMonths <= 18
                        ? 'text-warning'
                        : 'text-danger'
                )}
              >
                {calculations.paybackMonths.toFixed(1)} mo
              </span>
            </div>
          </div>

          {/* Deal Assessment */}
          {calculations.overallRating && (
            <div
              className={clsx(
                'p-3 rounded-lg text-center',
                calculations.overallRating.class === 'great' && 'bg-success/10 border border-success/30',
                calculations.overallRating.class === 'good' && 'bg-info/10 border border-info/30',
                calculations.overallRating.class === 'average' && 'bg-warning/10 border border-warning/30',
                calculations.overallRating.class === 'poor' && 'bg-danger/10 border border-danger/30'
              )}
            >
              <div
                className={clsx(
                  'text-[10px] font-medium uppercase tracking-wide mb-1',
                  calculations.overallRating.class === 'great' && 'text-success/70',
                  calculations.overallRating.class === 'good' && 'text-info/70',
                  calculations.overallRating.class === 'average' && 'text-warning/70',
                  calculations.overallRating.class === 'poor' && 'text-danger/70'
                )}
              >
                Deal Assessment
              </div>
              <div
                className={clsx(
                  'text-sm font-semibold',
                  calculations.overallRating.class === 'great' && 'text-success',
                  calculations.overallRating.class === 'good' && 'text-info',
                  calculations.overallRating.class === 'average' && 'text-warning',
                  calculations.overallRating.class === 'poor' && 'text-danger'
                )}
              >
                {calculations.overallRating.message}
              </div>
            </div>
          )}
        </>
      )}

      {!calculations && (
        <p className="text-[11px] text-[var(--text-muted)] text-center py-4">
          Add products to see quote summary.
        </p>
      )}
    </div>
  )
}
